"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { devLabel, devLabelPanel } from "@/lib/dev-labels";
import styles from "./OnboardingModal.module.css";

/**
 * OnboardingModal — Phase 25-AB Task 9 (panel 35 / oc-ob-overlay).
 *
 * Mockup source: docs/mockups/index.html lines 1245-1262.
 * 3-step wizard. Dots indicate progress. Skip + Prev/Next navigation.
 * Esc closes (treated as Skip).
 */

interface Step {
	title: string;
	body: string;
}

const DEFAULT_STEPS: Step[] = [
	{
		title: "makemonlab Studio (1 / 3)",
		body: "환영합니다. OpenCut 기반 AI 자동 편집 플랫폼에 오신 것을 환영합니다.",
	},
	{
		title: "makemonlab Studio (2 / 3)",
		body: "OpenCut 기반 AI 자동 편집 플랫폼. CapCut 수준 편집 + 50+ AI 마법사 한 곳에서.",
	},
	{
		title: "makemonlab Studio (3 / 3)",
		body: "단축키 ? 를 눌러 도움말을 열 수 있습니다. 시작해 보세요.",
	},
];

export interface OnboardingModalProps {
	open: boolean;
	onClose: () => void;
	steps?: Step[];
}

export function OnboardingModal({
	open,
	onClose,
	steps = DEFAULT_STEPS,
}: OnboardingModalProps) {
	const [step, setStep] = useState(0);
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	useEffect(() => {
		if (!open) {
			setStep(0);
			return;
		}
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") onClose();
		}
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [open, onClose]);

	if (!open || !mounted) return null;

	const current = steps[step] ?? steps[0];
	const isLast = step === steps.length - 1;

	function next() {
		if (isLast) onClose();
		else setStep((s) => Math.min(s + 1, steps.length - 1));
	}
	function prev() {
		setStep((s) => Math.max(s - 1, 0));
	}

	return createPortal(
		<div
			id="oc-ob-overlay"
			className={styles.overlay}
			role="dialog"
			aria-modal="true"
			aria-label="Onboarding"
			{...devLabelPanel("35 · Onboarding")}
		>
			<div className={styles.card}>
				<div className={styles.dots} {...devLabel("35-A")}>
					{steps.map((_, i) => (
						<span
							key={i}
							className={i === step ? styles.dotActive : styles.dot}
						/>
					))}
				</div>
				<div className={styles.title} {...devLabel("35-B")}>
					{current?.title}
				</div>
				<div className={styles.body} {...devLabel("35-C")}>
					{current?.body}
				</div>
				<div className={styles.actions}>
					<button
						type="button"
						className={styles.skip}
						onClick={onClose}
						{...devLabel("35-D")}
					>
						건너뛰기
					</button>
					<div className={styles.navGroup}>
						<button
							type="button"
							className={styles.prev}
							disabled={step === 0}
							onClick={prev}
							{...devLabel("35-E")}
						>
							이전
						</button>
						<button
							type="button"
							className={styles.next}
							onClick={next}
							{...devLabel("35-F")}
						>
							{isLast ? "시작" : "다음 →"}
						</button>
					</div>
				</div>
			</div>
		</div>,
		document.body,
	);
}
